import {
  AfterViewInit,
  Component,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthService } from '~app/services/auth.service';
import { RolePermissionService } from '~app/services/role-permission.service';

const StorageKeyPrefix = 'ciao.teamRoleModal.shown.';

// MatDialog.open(TemplateRef<T>) returns MatDialogRef<T> where T is the
// embedded-view context (we don't pass one, so it's `unknown`).

/**
 * One-time pop-up shown to Guest and Dispatcher users informing them about
 * the new Team roles. Persisted per user via localStorage so it doesn't
 * re-appear across sessions on the same device.
 */
@Component({
  selector: 'ciao-team-roles-popup',
  templateUrl: './team-roles-popup.component.html',
  styleUrls: ['./team-roles-popup.component.less'],
  standalone: false,
})
export class TeamRolesPopupComponent implements AfterViewInit, OnDestroy {
  @ViewChild('DialogTemplate') DialogTemplate: TemplateRef<unknown>;

  readonly helpLink = '/help/info/admin/roles-permissions';

  private dialogRef?: MatDialogRef<unknown>;
  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private rolePermissionService: RolePermissionService,
    private dialogService: MatDialog,
    private router: Router
  ) {}

  ngAfterViewInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.authService.currentUser$,
        this.rolePermissionService.isGuest$,
        this.rolePermissionService.isDispatcher$,
      ])
        .pipe(
          filter(([user]) => !!user?.personId),
          filter(([, isGuest, isDispatcher]) => isGuest || isDispatcher),
          take(1)
        )
        .subscribe(([user]) => {
          if (this.hasSeen(user.personId)) {
            return;
          }
          this.dialogRef = this.dialogService.open(this.DialogTemplate, {
            panelClass: 'ciao-team-roles-popup-panel',
            maxWidth: '100vw',
            maxHeight: '100vh',
            autoFocus: 'dialog',
            ariaLabelledBy: 'team-roles-popup-heading',
          });
          this.dialogRef.afterClosed().subscribe(() => {
            this.markSeen(user.personId);
          });
        })
    );
  }

  ngOnDestroy(): void {
    this.dialogRef?.close();
    this.subscriptions.unsubscribe();
  }

  onClose(): void {
    this.dialogRef?.close('manual close');
  }

  onCta(): void {
    this.dialogRef?.close('cta');
    this.router.navigateByUrl(this.helpLink);
  }

  private storageKey(personId: string): string {
    return `${StorageKeyPrefix}${personId}`;
  }

  private hasSeen(personId: string): boolean {
    try {
      return localStorage.getItem(this.storageKey(personId)) === 'true';
    } catch {
      return false;
    }
  }

  private markSeen(personId: string): void {
    try {
      localStorage.setItem(this.storageKey(personId), 'true');
    } catch {
      // localStorage disabled / quota exceeded — modal will reappear next login,
      // which is an acceptable degradation.
    }
  }
}
